import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import ModalWrapper from '../Modal/Modal';
import {defaultStyles} from '../Modal/Modal.config';
import Loading from '../Loading/Loading';
import {
  AddButton,
  Body,
  BoxLabel,
  BoxList,
  BoxNumber,
  BoxPrice,
  BoxRow,
  BoxWeight,
  CloseButton,
  Footer,
  Header,
  Heading,
  Intro,
  Message,
  PickerWrapper,
  PricePerKg,
  ProductName,
  Title,
} from './BoxPicker.style';
import {pickBoxes, updateLineBoxes} from '../../actions/basket.action';
import {closeBoxPicker, dismissBoxPicker} from '../../actions/modal.action';
import {
  findProductName,
  formatKg,
  formatPounds,
  getBoxPrice,
  getLineBoxRows,
  isBoxPickerEnabled,
  sumBoxWeights,
} from '../../helpers/boxes.helper';
import {BOX_PICKER_COPY} from '../../constants/constants';

const renderBoxRow = ({box, unitPrice, checked, disabled, onToggle}) => (
  <BoxRow
    key={box.boxNumber}
    $disabled={disabled}
    data-rw={`box-row-${box.boxNumber}`}
  >
    <BoxLabel $disabled={disabled}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle && onToggle(box.boxNumber)}
      />
      <BoxWeight>
        {box.weightKg !== undefined ? formatKg(box.weightKg) : box.boxNumber}
        <BoxNumber>{box.boxNumber}</BoxNumber>
      </BoxWeight>
      <BoxPrice>
        {box.weightKg !== undefined &&
          formatPounds(getBoxPrice(box.weightKg, unitPrice))}
      </BoxPrice>
    </BoxLabel>
  </BoxRow>
);

// The picker's content, kept free of redux and the modal so it can be tested
// with a plain render.
export const BoxPickerContent = ({
  picker,
  itemName,
  selected,
  removedHeld = [],
  onToggle,
  onAdd,
  onClose,
  onOk,
  onRemoveHeld,
  removingHeld,
}) => {
  // A refused box has gone from the fresh list, so its message shows above.
  const {boxes, line, error, picking} = picker;
  const unitPrice = (line && line.unitPrice) || 0;
  // picker.line never refreshes after a plain box removal (only a new pick
  // or peek replaces it), so boxes the customer has unticked this session
  // are filtered out here rather than relying on it to catch up.
  const heldRows = getLineBoxRows(line).filter(
    box => removedHeld.indexOf(box.boxNumber) === -1
  );
  const heldNumbers = heldRows.map(box => box.boxNumber);
  const listed = ((boxes && boxes.boxes) || []).filter(
    box => heldNumbers.indexOf(box.boxNumber) === -1
  );
  const isPromise = !!(boxes && boxes.promiseOnOrder);
  const selectedBoxes = listed.filter(
    box => selected.indexOf(box.boxNumber) > -1
  );
  const selectedKg = sumBoxWeights(selectedBoxes);
  const selectedPrice = getBoxPrice(selectedKg, unitPrice);
  const header = (
    <Header>
      <Heading>
        <Title>{BOX_PICKER_COPY.TITLE}</Title>
        {itemName && <ProductName>{itemName}</ProductName>}
        {!!unitPrice && (
          <PricePerKg>
            {formatPounds(unitPrice)} {BOX_PICKER_COPY.PER_KG}
          </PricePerKg>
        )}
      </Heading>
      <CloseButton
        type="button"
        aria-label={BOX_PICKER_COPY.CLOSE}
        onClick={onClose}
        data-rw="box-picker-close"
      >
        &times;
      </CloseButton>
    </Header>
  );

  // No list to show: nothing in stock (a promise) or the list couldn't load.
  if (isPromise || !boxes) {
    return (
      <PickerWrapper>
        {header}
        <Body>
          <Message $isError={!isPromise} data-rw="box-picker-message">
            {isPromise ? BOX_PICKER_COPY.PROMISE : error}
          </Message>
        </Body>
        <Footer>
          <AddButton onClick={onOk} data-rw="box-picker-ok">
            {BOX_PICKER_COPY.OK}
          </AddButton>
        </Footer>
      </PickerWrapper>
    );
  }

  const addDisabled = picking || removingHeld || selectedBoxes.length === 0;
  const addLabel = picking ? (
    <Loading isLight={true} />
  ) : (
    `${BOX_PICKER_COPY.ADD_BOXES(selectedBoxes.length)}${
      selectedBoxes.length
        ? ` (${formatKg(selectedKg)}, ${formatPounds(selectedPrice)})`
        : ''
    }`
  );
  return (
    <PickerWrapper>
      {header}
      <Body>
        <Intro>{BOX_PICKER_COPY.INTRO}</Intro>
        {error && (
          <Message $isError={true} data-rw="box-picker-message">
            {error}
          </Message>
        )}
        <BoxList>
          {heldRows.map(box =>
            renderBoxRow({
              box,
              unitPrice,
              checked: true,
              disabled: removingHeld,
              // Unticking an already-held box removes it straight away,
              // same as the basket page's "Remove" link - it's already
              // saved, not part of the additive pick below.
              onToggle: onRemoveHeld,
            })
          )}
          {listed.map(box =>
            renderBoxRow({
              box,
              unitPrice,
              checked: selected.indexOf(box.boxNumber) > -1,
              disabled: picking || removingHeld,
              onToggle,
            })
          )}
        </BoxList>
      </Body>
      <Footer>
        <AddButton
          onClick={addDisabled ? undefined : onAdd}
          $disabled={addDisabled}
          $isLoading={picking}
          data-rw="box-picker-add"
        >
          {addLabel}
        </AddButton>
      </Footer>
    </PickerWrapper>
  );
};

BoxPickerContent.propTypes = {
  picker: PropTypes.object.isRequired,
  itemName: PropTypes.string,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  removedHeld: PropTypes.arrayOf(PropTypes.string),
  onToggle: PropTypes.func,
  onAdd: PropTypes.func,
  onClose: PropTypes.func,
  onOk: PropTypes.func,
  onRemoveHeld: PropTypes.func,
  removingHeld: PropTypes.bool,
};

const getListKey = picker =>
  ((picker && picker.boxes && picker.boxes.boxes) || [])
    .map(box => box.boxNumber)
    .join(',');

const BoxPicker = () => {
  const dispatch = useDispatch();
  const picker = useSelector(state => state.modal && state.modal.boxPicker);
  const itemName = useSelector(state =>
    findProductName(state, picker && picker.itemId)
  );
  const itemId = picker && picker.itemId;
  const removingHeld = useSelector(
    state => !!state.loading && !!state.loading.updatingBasket
  );
  const [selected, setSelected] = useState([]);
  // Held boxes the customer has unticked this session - see the comment on
  // BoxPickerContent's heldRows for why this is tracked locally instead of
  // read back from the picker.
  const [removedHeld, setRemovedHeld] = useState([]);
  const listKey = getListKey(picker);

  // A new picker, or a fresh list after a refusal, starts with nothing ticked.
  // Depends on strings only: the picker object is rebuilt on every update.
  useEffect(() => {
    setSelected([]);
    setRemovedHeld([]);
  }, [itemId, listKey]);

  if (!picker || !isBoxPickerEnabled()) {
    return null;
  }
  const onToggle = boxNumber =>
    setSelected(current =>
      current.indexOf(boxNumber) > -1
        ? current.filter(number => number !== boxNumber)
        : [...current, boxNumber]
    );
  const onAdd = () => {
    if (!selected.length || picker.picking) {
      return;
    }
    const unitPrice = (picker.line && picker.line.unitPrice) || 0;
    const listed = (picker.boxes && picker.boxes.boxes) || [];
    const quantity = sumBoxWeights(
      listed.filter(box => selected.indexOf(box.boxNumber) > -1)
    );
    dispatch(
      pickBoxes(picker.itemId, selected, {
        quantity,
        value: getBoxPrice(quantity, unitPrice),
      })
    );
  };
  const onClose = () => dispatch(dismissBoxPicker());
  // Keeps the ordinary kg line: at checkout the server promises a box.
  const onKeep = () => dispatch(closeBoxPicker());
  const onRemoveHeld = boxNumber => {
    const line = picker.line;
    if (!line || !line.uuid || removingHeld) {
      return;
    }
    // Relative to the full original list, not just this one box, so a
    // second removal in the same session doesn't resurrect an earlier one
    // that picker.line hasn't caught up with yet.
    const remaining = getLineBoxRows(line)
      .map(box => box.boxNumber)
      .filter(
        number => number !== boxNumber && removedHeld.indexOf(number) === -1
      );
    setRemovedHeld(current => [...current, boxNumber]);
    dispatch(updateLineBoxes(line.uuid, itemId, remaining));
  };
  return (
    <ModalWrapper
      customStyles={defaultStyles}
      isOpen={true}
      closeModal={onClose}
    >
      <BoxPickerContent
        picker={picker}
        itemName={itemName}
        selected={selected}
        removedHeld={removedHeld}
        onToggle={onToggle}
        onAdd={onAdd}
        onClose={onClose}
        onOk={onKeep}
        onRemoveHeld={onRemoveHeld}
        removingHeld={removingHeld}
      />
    </ModalWrapper>
  );
};

export default BoxPicker;
